import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Clock, Users, Video, CheckCircle, XCircle, AlertCircle, RotateCcw, User, Plus, CreditCard as Edit, Trash2, ChevronLeft, ChevronRight, X, Wifi, WifiOff, Cloud, Download, Copy, Check } from 'lucide-react';
import { Meeting } from '../types';
import { meetingsService, contactsService } from '../services/supabaseService';
import authService from '../services/authService';
import MeetingModal from './MeetingModal';
// import GoogleAuthButton from './GoogleAuthButton'; // DEPRECATED: Using Calendly
import RealtimeToast, { ToastMessage } from './RealtimeToast';
import { useRealtimeMeetings, useRealtimeMeetingListener } from '../hooks/useRealtimeMeetings';
import { calendlyService, CalendlyEvent } from '../services/calendlyService';

const InternalCalendar: React.FC = () => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [calendlyEvents, setCalendlyEvents] = useState<CalendlyEvent[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isNewMeetingOpen, setIsNewMeetingOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [showMeetingDetails, setShowMeetingDetails] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'programada'>('all');
  const [toastMessages, setToastMessages] = useState<ToastMessage[]>([]);
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  const [copiedMeetingLink, setCopiedMeetingLink] = useState(false);

  // Get current user info
  const currentSession = authService.getCurrentSession();
  const currentUser = currentSession?.user;
  const isAdmin = authService.isSuperAdmin();

  // Handle toast messages
  const handleToast = useCallback((message: ToastMessage) => {
    setToastMessages(prev => [...prev, message]);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      setToastMessages(prev => prev.filter(m => m.id !== message.id));
    }, 5000);
  }, []);

  const handleCloseToast = useCallback((id: string) => {
    setToastMessages(prev => prev.filter(m => m.id !== id));
  }, []);

  // Realtime subscription
  const { isConnected } = useRealtimeMeetings({
    onToast: handleToast,
    enabled: realtimeEnabled
  });

  // Handle realtime events
  const handleRealtimeEvent = useCallback((event: { eventType: 'INSERT' | 'UPDATE' | 'DELETE'; meeting: any }) => {
    console.log('🔄 Recibido evento de Realtime, recargando datos...', event.eventType);
    loadData();
  }, []);

  useRealtimeMeetingListener(handleRealtimeEvent, realtimeEnabled);

  useEffect(() => {
    // Recarga cuando cambia el mes visible para que el rango de calendly
    // events se ajuste al mes elegido.
    loadData();
  }, [selectedDate.getFullYear(), selectedDate.getMonth()]);

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando datos del calendario...');
      
      // Cargamos Calendly events del mes visible ± 1 mes para permitir
      // navegar rápido a mes anterior/siguiente sin volver a pedir. Antes se
      // pedían los últimos 100 events sin filtro de fecha, lo que en cuentas
      // activas cortaba silenciosamente eventos del mes actual.
      const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
      const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 2, 1);

      const [meetingsData, contactsData, calendlyData] = await Promise.allSettled([
        meetingsService.getAll(),
        contactsService.getAll(),
        calendlyService.getEventsInRange(monthStart, monthEnd)
      ]);

      const meetings = meetingsData.status === 'fulfilled' ? meetingsData.value : [];
      const contacts = contactsData.status === 'fulfilled' ? contactsData.value : [];
      const cEvents = calendlyData.status === 'fulfilled' ? calendlyData.value : [];

      setMeetings(meetings);
      setContacts(contacts);
      setCalendlyEvents(cEvents);
      
      console.log('✅ Datos del calendario cargados:', {
        meetings: meetings.length,
        contacts: contacts.length
      });
    } catch (error) {
      console.error('Error loading calendar data:', error);
      setMeetings([]);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMeeting = async (meetingData: any) => {
    try {
      if (editingMeeting) {
        // Update existing meeting
        await meetingsService.update(editingMeeting.id, meetingData);
        console.log('✅ Reunión actualizada');
      } else {
        // Create new meeting - this is handled in MeetingModal
        console.log('✅ Nueva reunión creada');
      }
      
      // Reload meetings to reflect changes
      await loadData();
      setIsNewMeetingOpen(false);
      setEditingMeeting(null);
    } catch (error) {
      console.error('Error saving meeting:', error);
      alert('Error al guardar la reunión');
    }
  };

  const handleEditMeeting = (meeting: Meeting) => {
    setEditingMeeting(meeting);
    setIsNewMeetingOpen(true);
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    const meeting = meetings.find(m => m.id === meetingId);
    if (meeting && window.confirm(`¿Eliminar la reunión "${meeting.title}"? Esta acción no se puede deshacer.`)) {
      try {
        console.log('🔄 Eliminando reunión:', meetingId);
        await meetingsService.delete(meetingId);
        
        // Update local state immediately for instant UI feedback
        const updatedMeetings = meetings.filter(m => m.id !== meetingId);
        setMeetings(updatedMeetings);
        
        // Close details modal if this meeting was selected
        if (selectedMeeting?.id === meetingId) {
          setShowMeetingDetails(false);
          setSelectedMeeting(null);
        }
        
        // Also reload data to ensure synchronization
        await loadData();
        
        console.log('✅ Reunión eliminada correctamente');
        alert('✅ Reunión eliminada correctamente');
      } catch (error) {
        console.error('Error deleting meeting:', error);
        // Reload data on error to restore correct state
        await loadData();
        alert('❌ Error al eliminar la reunión: ' + error.message);
      }
    }
  };

  const handleApproveMeeting = async (meetingId: string) => {
    try {
      console.log('✅ Aprobando reunión:', meetingId);
      await meetingsService.update(meetingId, {
        approval_status: 'approved',
        approved_by: currentUser?.id,
        approved_at: new Date()
      });
      await loadData();
      alert('✅ Reunión aprobada correctamente');
    } catch (error) {
      console.error('Error approving meeting:', error);
      alert('❌ Error al aprobar la reunión: ' + error.message);
    }
  };

  const handleRejectMeeting = async (meetingId: string) => {
    const reason = window.prompt('Motivo del rechazo (opcional):');
    try {
      console.log('❌ Rechazando reunión:', meetingId, 'motivo:', reason);
      await meetingsService.update(meetingId, {
        approval_status: 'rejected',
        rejection_reason: reason || undefined,
        approved_by: currentUser?.id,
        approved_at: new Date()
      });
      await loadData();
      alert('✅ Reunión rechazada');
    } catch (error) {
      console.error('Error rejecting meeting:', error);
      alert('❌ Error al rechazar la reunión: ' + error.message);
    }
  };

  const handleDayClick = (date: Date) => {
    const dayMeetings = getMeetingsForDate(date);
    if (dayMeetings.length > 0) {
      // If there's only one meeting, show its details
      if (dayMeetings.length === 1) {
        setSelectedMeeting(dayMeetings[0]);
        setShowMeetingDetails(true);
      } else {
        // If multiple meetings, show a list to choose from
        setSelectedDate(date);
        setViewMode('day');
      }
    } else {
      // No meetings, just select the date for potential new meeting
      setSelectedDate(date);
    }
  };

  const getMeetingsForDate = (date: Date): Meeting[] => {
    return meetings.filter(meeting => {
      const meetingDate = new Date(meeting.datetime);
      return meetingDate.toDateString() === date.toDateString();
    });
  };

  const getCalendlyEventsForDate = (date: Date): CalendlyEvent[] => {
    return calendlyEvents.filter(ev => {
      if (ev.status === 'canceled') return false;
      return new Date(ev.start_time).toDateString() === date.toDateString();
    });
  };

  const getFilteredMeetings = () => {
    let filtered = meetings;
    const now = new Date();

    // Filter out past meetings
    filtered = filtered.filter(meeting => new Date(meeting.datetime) >= now);

    // Apply approval status filter
    if (filter === 'pending') {
      filtered = filtered.filter(m => m.approvalStatus === 'pending');
    } else if (filter === 'approved') {
      filtered = filtered.filter(m => m.approvalStatus === 'approved');
    } else if (filter === 'programada') {
      filtered = filtered.filter(m => m.status === 'programada');
    }

    // Apply user filter for non-admins
    if (!isAdmin) {
      filtered = filtered.filter(meeting => 
        meeting.assignedUserId === currentUser?.id || 
        meeting.owner === currentUser?.name
      );
    }

    return filtered;
  };

  const generateCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      days.push(date);
    }
    
    return days;
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setSelectedDate(newDate);
  };

  const getStatusIcon = (status: Meeting['status']) => {
    switch (status) {
      case 'programada': return Clock;
      case 'realizada': return CheckCircle;
      case 'cancelada': return XCircle;
      case 'reprogramada': return RotateCcw;
      case 'no_asistio': return AlertCircle;
      default: return Clock;
    }
  };

  const getStatusColor = (status: Meeting['status']) => {
    switch (status) {
      case 'programada': return 'text-blue-600 bg-blue-100';
      case 'realizada': return 'text-green-600 bg-green-100';
      case 'cancelada': return 'text-red-600 bg-red-100';
      case 'reprogramada': return 'text-orange-600 bg-orange-100';
      case 'no_asistio': return 'text-gray-600 bg-gray-100';
      default: return 'text-blue-600 bg-blue-100';
    }
  };

  const getStatusLabel = (status: Meeting['status']) => {
    switch (status) {
      case 'programada': return 'Programada';
      case 'realizada': return 'Realizada';
      case 'cancelada': return 'Cancelada';
      case 'reprogramada': return 'Reprogramada';
      case 'no_asistio': return 'No Asistió';
      default: return 'Programada';
    }
  };

  // Get random motivational message for empty calendar
  const getRandomCalendarMessage = () => {
    const messages = [
      "☕ ¡Perfecto! Agenda libre = más tiempo para el café y la estrategia",
      "🎯 Sin reuniones = enfoque total en lo que realmente importa",
      "💪 Calendario despejado = productividad al máximo",
      "🚀 Sin meetings = más tiempo para hacer que las cosas sucedan",
      "⚡ Agenda libre = oportunidad perfecta para la creatividad",
      "🌟 Sin interrupciones = momento ideal para brillar",
      "🔥 Calendario vacío = energía concentrada en tus objetivos",
      "💡 Sin reuniones = espacio mental para grandes ideas",
      "🏆 Agenda despejada = día perfecto para ser productivo",
      "🎉 Sin meetings = más tiempo para hacer magia en el CRM"
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  // Get random message for today's meetings
  const getRandomTodayMessage = () => {
    const messages = [
      "☕ Momento perfecto para un café mientras planificas el día",
      "🎯 Sin reuniones hoy = día ideal para enfocarte en tus proyectos",
      "💼 Agenda libre = oportunidad para avanzar en tareas importantes",
      "🚀 Sin meetings = productividad sin interrupciones",
      "⚡ Día despejado = energía concentrada en lo que importa",
      "🌟 Sin reuniones = momento perfecto para la creatividad",
      "🔥 Agenda libre = día ideal para hacer que las cosas sucedan",
      "💪 Sin interrupciones = oportunidad para dar lo mejor de ti"
    ];
    return messages[Math.floor(Math.random() * messages.length)];
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();
  const filteredMeetings = getFilteredMeetings();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando calendario...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Realtime Toast Notifications */}
      <RealtimeToast
        messages={toastMessages}
        onClose={handleCloseToast}
        onNavigate={(meetingId) => {
          const meeting = meetings.find(m => m.id === meetingId);
          if (meeting) {
            setSelectedMeeting(meeting);
            setShowMeetingDetails(true);
          }
        }}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-gray-900">
              {isAdmin ? 'Calendario General' : 'Mi Calendario'}
            </h1>
            {/* Realtime Status Indicator */}
            <div className="flex items-center space-x-2">
              {isConnected ? (
                <div className="flex items-center space-x-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                  <Wifi className="w-3 h-3" />
                  <span>Tiempo real activo</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                  <WifiOff className="w-3 h-3" />
                  <span>Desconectado</span>
                </div>
              )}
            </div>
          </div>
          <p className="text-gray-600">
            {isAdmin ? 'Todas las reuniones del equipo' : 'Tus reuniones y eventos'}
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setIsNewMeetingOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Nueva Reunión</span>
          </button>
        </div>
      </div>

      {/* DEPRECATED: Google Calendar — now using Calendly */}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Filtrar:</span>
          <div className="flex space-x-2">
            {[
              { value: 'all', label: 'Todas' },
              { value: 'pending', label: 'Pendientes de Aprobación' },
              { value: 'approved', label: 'Aprobadas' },
              { value: 'programada', label: 'Programadas' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value as any)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  filter === option.value
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            {/* Calendar Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedDate.toLocaleDateString('es-ES', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigateMonth('prev')}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setSelectedDate(new Date())}
                    className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Hoy
                  </button>
                  <button
                    onClick={() => navigateMonth('next')}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="p-6">
              {/* Days of week header */}
              <div className="grid grid-cols-7 gap-1 mb-4">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((date, index) => {
                  const isCurrentMonth = date.getMonth() === selectedDate.getMonth();
                  const isToday = date.toDateString() === today.toDateString();
                  const dayMeetings = getMeetingsForDate(date);
                  const dayCalendly = getCalendlyEventsForDate(date);
                  const totalEvents = dayMeetings.length + dayCalendly.length;

                  return (
                    <button
                      key={index}
                      onClick={() => handleDayClick(date)}
                      className={`
                        h-20 p-1 text-left border border-gray-200 rounded-lg transition-all hover:bg-blue-50 hover:border-blue-300
                        ${!isCurrentMonth ? 'text-gray-300 bg-gray-50' : 'text-gray-900'}
                        ${isToday ? 'bg-blue-100 border-blue-300 font-semibold' : ''}
                        ${totalEvents > 0 ? 'cursor-pointer' : ''}
                      `}
                    >
                      <div className="text-sm font-medium mb-1">{date.getDate()}</div>

                      {/* Event indicators */}
                      <div className="space-y-0.5">
                        {dayCalendly.slice(0, 2).map(ev => (
                          <div key={ev.id} className="text-xs px-1 py-0.5 rounded truncate bg-indigo-100 text-indigo-800">
                            {(ev.event_type_name || 'Calendly').substring(0, 12)}
                          </div>
                        ))}
                        {dayMeetings.slice(0, Math.max(0, 2 - dayCalendly.length)).map(meeting => (
                          <div
                            key={meeting.id}
                            className={`text-xs px-1 py-0.5 rounded truncate ${
                              meeting.approvalStatus === 'approved' ? 'bg-green-100 text-green-800' :
                              meeting.approvalStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {meeting.title.length > 12 ? meeting.title.substring(0, 12) + '...' : meeting.title}
                          </div>
                        ))}
                        {totalEvents > 2 && (
                          <div className="text-xs text-gray-500">+{totalEvents - 2} más</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Calendly Upcoming */}
        {calendlyEvents.filter(e => e.status === 'active' && new Date(e.start_time) >= new Date()).length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-indigo-200 mb-4">
            <div className="p-4 border-b border-indigo-100 bg-indigo-50 rounded-t-lg">
              <h3 className="text-sm font-semibold text-indigo-900 flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>Próximas via Calendly</span>
              </h3>
            </div>
            <div className="p-3 space-y-2 max-h-48 overflow-y-auto">
              {calendlyEvents
                .filter(e => e.status === 'active' && new Date(e.start_time) >= new Date())
                .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
                .slice(0, 5)
                .map(ev => (
                  <div key={ev.id} className="flex items-center justify-between p-2 bg-indigo-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-gray-900 truncate">{ev.event_type_name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(ev.start_time).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                        {' · '}{new Date(ev.start_time).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                        {ev.invitee_name && ` · ${ev.invitee_name}`}
                      </p>
                    </div>
                    {ev.location_url && (
                      <a href={ev.location_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-indigo-600 hover:text-indigo-800 flex-shrink-0">
                        <Video className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Meetings List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {isAdmin ? 'Todas las Reuniones' : 'Mis Reuniones'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {filteredMeetings.length} reuniones
            </p>
          </div>
          
          <div className="p-6 max-h-96 overflow-y-auto">
            {filteredMeetings.length > 0 ? (
              <div className="space-y-4">
                {filteredMeetings
                  .sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime())
                  .map((meeting) => {
                    const contact = contacts.find(c => c.id === meeting.contactId);
                    const StatusIcon = getStatusIcon(meeting.status);
                    const statusColor = getStatusColor(meeting.status);
                    const needsApproval = meeting.approvalStatus === 'pending';
                    const isFromGoogle = meeting.google_event_id || meeting.sync_source === 'google';

                    return (
                      <div
                        key={meeting.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow cursor-pointer"
                        onClick={() => {
                          setSelectedMeeting(meeting);
                          setShowMeetingDetails(true);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className={`p-2 rounded-lg ${isFromGoogle ? 'bg-blue-100 text-blue-600' : statusColor}`}>
                              {isFromGoogle ? (
                                <Cloud className="w-4 h-4" />
                              ) : (
                                <StatusIcon className="w-4 h-4" />
                              )}
                            </div>

                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <h4 className="text-sm font-medium text-gray-900">{meeting.title}</h4>
                                {isFromGoogle && (
                                  <span className="inline-flex items-center space-x-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-700 border border-blue-200">
                                    <Download className="w-3 h-3" />
                                    <span>Google</span>
                                  </span>
                                )}
                                {needsApproval && !isFromGoogle && (
                                  <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                    Pendiente
                                  </span>
                                )}
                                {meeting.approvalStatus === 'approved' && !isFromGoogle && (
                                  <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    Aprobada
                                  </span>
                                )}
                              </div>
                              
                              <div className="text-xs text-gray-600 space-y-1">
                                <div className="flex items-center space-x-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>
                                    {new Date(meeting.datetime).toLocaleDateString()} •
                                    {new Date(meeting.datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                </div>

                                <div className="flex items-center space-x-1">
                                  <User className="w-3 h-3" />
                                  <span>{contact?.fullName || meeting.clientName || 'Cliente no encontrado'}</span>
                                </div>

                                <div className="flex items-center space-x-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{meeting.duration} minutos</span>
                                </div>

                                {isFromGoogle && meeting.participants && meeting.participants.length > 0 && (
                                  <div className="flex items-center space-x-1 text-blue-600">
                                    <Users className="w-3 h-3" />
                                    <span>
                                      Con: {meeting.participants.slice(0, 2).join(', ')}
                                      {meeting.participants.length > 2 && ` +${meeting.participants.length - 2}`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">☕</div>
                <h4 className="text-lg font-medium text-gray-900 mb-2">
                  {filter === 'pending' ? 'No hay reuniones pendientes' : 
                   filter === 'approved' ? 'No hay reuniones aprobadas' :
                   isAdmin ? 'Sin reuniones próximas' : 'Tu agenda está libre'}
                </h4>
                <p className="text-gray-600 mb-2">
                  {filter === 'all' ? getRandomCalendarMessage() : 'Cambia el filtro para ver otras reuniones'}
                </p>
                <p className="text-xs text-gray-400">
                  Las próximas reuniones aparecerán aquí cuando se programen
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Meeting Details Modal */}
      {showMeetingDetails && selectedMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Detalles de la Reunión</h2>
              <button
                onClick={() => {
                  setShowMeetingDetails(false);
                  setSelectedMeeting(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Meeting Info */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">{selectedMeeting.title}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>
                      {new Date(selectedMeeting.datetime).toLocaleDateString()} • 
                      {new Date(selectedMeeting.datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{selectedMeeting.duration} minutos</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span>{contacts.find(c => c.id === selectedMeeting.contactId)?.fullName || selectedMeeting.clientName || 'Cliente'}</span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span>{selectedMeeting.participants.join(', ')}</span>
                  </div>
                </div>
                
                {selectedMeeting.description && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Descripción</h4>
                    <p className="text-gray-700">{selectedMeeting.description}</p>
                  </div>
                )}
                
                {selectedMeeting.meetLink && (
                  <div className="mt-4 space-y-3">
                    <a
                      href={selectedMeeting.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      <Video className="w-4 h-4" />
                      <span>Unirse a la reunión</span>
                    </a>

                    <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 mb-1">URL de la reunión</p>
                        <p className="text-sm text-gray-600 truncate">{selectedMeeting.meetLink}</p>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedMeeting.meetLink);
                          setCopiedMeetingLink(true);
                          setTimeout(() => setCopiedMeetingLink(false), 2000);
                        }}
                        className="flex-shrink-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Copiar URL"
                      >
                        {copiedMeetingLink ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  {selectedMeeting.approvalStatus === 'pending' && isAdmin && (
                    <>
                      <button 
                        onClick={() => {
                          handleApproveMeeting(selectedMeeting.id);
                          setShowMeetingDetails(false);
                        }}
                        className="text-sm text-green-600 hover:text-green-800 px-3 py-1 border border-green-200 rounded-lg hover:bg-green-50 transition-colors"
                      >
                        Aprobar
                      </button>
                      <button 
                        onClick={() => {
                          handleRejectMeeting(selectedMeeting.id);
                          setShowMeetingDetails(false);
                        }}
                        className="text-sm text-red-600 hover:text-red-800 px-3 py-1 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        Rechazar
                      </button>
                    </>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setEditingMeeting(selectedMeeting);
                      setShowMeetingDetails(false);
                      setIsNewMeetingOpen(true);
                    }}
                    className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-800 px-3 py-1 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <Edit className="w-3 h-3" />
                    <span>Editar</span>
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteMeeting(selectedMeeting.id);
                      setShowMeetingDetails(false);
                    }}
                    className="flex items-center space-x-1 text-sm text-red-600 hover:text-red-800 px-3 py-1 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-3 h-3" />
                    <span>Eliminar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Today's Meetings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Reuniones de Hoy</h3>
        </div>
        
        <div className="p-6">
          {(() => {
            const todayMeetings = getMeetingsForDate(today).filter(meeting => {
              if (!isAdmin) {
                return meeting.assignedUserId === currentUser?.id || meeting.owner === currentUser?.name;
              }
              return true;
            });
            
            return todayMeetings.length > 0 ? (
              <div className="space-y-3">
                {todayMeetings.map((meeting) => {
                  const contact = contacts.find(c => c.id === meeting.contactId);
                  const StatusIcon = getStatusIcon(meeting.status);
                  const statusColor = getStatusColor(meeting.status);
                  const needsApproval = meeting.approvalStatus === 'pending' || meeting.approval_status === 'pending';
                  return (
                    <div 
                      key={meeting.id} 
                      className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => {
                        setSelectedMeeting(meeting);
                        setShowMeetingDetails(true);
                      }}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <div className={`p-1 rounded ${statusColor}`}>
                          <StatusIcon className="w-3 h-3" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(meeting.datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-1">{meeting.title}</p>
                      <p className="text-xs text-gray-500">
                        {contact?.fullName || meeting.clientName || 'Cliente'}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-3">☕</div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Sin reuniones hoy</h4>
                <p className="text-gray-600 text-xs">
                  {getRandomTodayMessage()}
                </p>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Meeting Modal */}
      <MeetingModal
        isOpen={isNewMeetingOpen}
        onClose={() => {
          setIsNewMeetingOpen(false);
          setEditingMeeting(null);
        }}
        onSave={handleSaveMeeting}
        meeting={editingMeeting}
      />
    </div>
  );
};

export default InternalCalendar;